import React, { useEffect, useState } from "react";
import { DatePicker, Drawer, Select, Radio, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs, { Dayjs } from "dayjs";
import { AppDispatch } from "../../../core/data/redux/store";
import { status as statusOptions } from "../../../core/common/selectoption/selectoption";
import { createBudget, fetchAllBudgets, updateBudget } from "../../../core/data/redux/payroll/Accounting/budgets/budgetsAction";
import { fetchAllCategories } from "../../../core/data/redux/payroll/Accounting/expensesCategories/expensesCategoriesAction";
import { fetchAllProjects } from "../../../core/data/redux/projects/project/projectAction";
import { filterByStatus } from "../../../core/utils/filterActiveData";

interface BudgetFormData {
  budgetTitle: string;
  budgetType: "project" | "category";
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  overallRevenue: string;
  overallExpense: string;
  tax: string;
  status: string;
  createdBy: number;
  project_id: undefined;
  category_id: undefined;
}

const BudgetsModal: React.FC<any> = ({ modalType, handleCancel, isModalVisible, selectedRowData, setTableKey }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { loadingStates } = useSelector((state: any) => state.accounting.budget);
  const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;
  const { categories } = useSelector((state: any) => state.accounting.expensesCategories);
  const { projectsData } = useSelector((state: any) => state.project.projects);
  const activeProjectsData = filterByStatus(projectsData, 'active', 'status');
  const activecategoriesData = filterByStatus(categories, 'active', 'status');
  const [errors, setErrors] = useState<any>({});

  const [formData, setFormData] = useState<BudgetFormData>({
    budgetTitle: "",
    budgetType: "project",
    startDate: null,
    endDate: null,
    overallRevenue: "",
    overallExpense: "",
    tax: "",
    status: "",
    createdBy: user?.id || 1,
    project_id: undefined,
    category_id: undefined,
  });

  const resetForm = () => {
    setFormData({
      budgetTitle: "",
      budgetType: "project",
      startDate: null,
      endDate: null,
      overallRevenue: "",
      overallExpense: "",
      tax: "",
      status: "",
      createdBy: user?.id || 1,
      project_id: undefined,
      category_id: undefined,
    });
    setErrors({});
  };


  useEffect(() => {
    dispatch(fetchAllCategories());
    dispatch(fetchAllProjects())
    if (modalType === "edit" && selectedRowData) {
      setFormData({
        ...selectedRowData,
        startDate: dayjs(selectedRowData.startDate),
        endDate: dayjs(selectedRowData.endDate),
      });
    } else {
      setFormData({
        budgetTitle: "",
        budgetType: "project",
        startDate: null,
        endDate: null,
        overallRevenue: "",
        overallExpense: "",
        tax: "",
        status: "",
        createdBy: user?.id || 1,
        project_id: undefined,
        category_id: undefined,
      });
    }
  }, [modalType, selectedRowData]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };
  const validateForm = () => {
    const newErrors: any = {};
    let isValid = true;

    if (!formData.budgetTitle.trim()) {
      newErrors.budgetTitle = "Budget title is required.";
      isValid = false;
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required.";
      isValid = false;
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required.";
      isValid = false;
    }
    if (formData.startDate && formData.endDate && formData.startDate.isAfter(formData.endDate)) {
      newErrors.startDate = "Start date cannot be after end date.";
      newErrors.endDate = "End date must be after start date.";
      isValid = false;
    }

    if (!formData.overallRevenue || isNaN(Number(formData.overallRevenue))) {
      newErrors.overallRevenue = "Valid revenue is required.";
      isValid = false;
    }

    if (!formData.overallExpense || isNaN(Number(formData.overallExpense))) {
      newErrors.overallExpense = "Valid expense is required.";
      isValid = false;
    }

    if (!formData.tax || isNaN(Number(formData.tax))) {
      newErrors.tax = "Valid tax is required.";
      isValid = false;
    }

    if (!formData.status) {
      newErrors.status = "Status is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };


  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    const payload = {
      ...formData,
      startDate: formData.startDate?.format("YYYY-MM-DD"),
      endDate: formData.endDate?.format("YYYY-MM-DD"),
      overallRevenue: parseFloat(formData.overallRevenue),
      overallExpense: parseFloat(formData.overallExpense),
      tax: parseFloat(formData.tax),
    };

    if (modalType === "edit") {
      const payloadWithModifiedBy = {
        ...payload,
        modifiedBy: user?.id || 1,
      };
      dispatch(updateBudget({ id: selectedRowData.budget_id, data: payloadWithModifiedBy }));
      dispatch(fetchAllBudgets())
      setTableKey((prevKey: any) => prevKey + 1);
      message.success("Budget updated successfully");
    } else {
      dispatch(createBudget(payload));
      dispatch(fetchAllBudgets())
      setTableKey((prevKey: any) => prevKey + 1);
      message.success("Budget created successfully");
    }

    handleCancel(); // Close modal
  };

  const getModalContainer = () => document.getElementById("modal-datepicker") || document.body;

  return (
    <Drawer
      title={modalType === "edit" ? "Edit Budget" : "Add Budget"}
      placement="right"
      width={600}
      onClose=
      {() => {
        handleCancel();
        resetForm();
      }}
      visible={isModalVisible}
      style={{ marginTop: 50, paddingBottom: 50 }}
    >
      <form>
        <div className="modal-body pb-0">
          <div className="mb-3">
            <label className="form-label">Budget Title</label>
            <input
              type="text"
              className={`form-control ${errors.budgetTitle ? "is-invalid" : ""}`}
              value={formData.budgetTitle}
              onChange={(e) => handleChange("budgetTitle", e.target.value)}
            />
            {errors.budgetTitle && <div className="invalid-feedback">{errors.budgetTitle}</div>}
          </div>
          <div className="mb-3">
            <label className="form-label">Budget Type</label>
            <Radio.Group
              onChange={(e) => handleChange("budgetType", e.target.value)}
              value={formData.budgetType}
              className="d-flex gap-4"
            >
              <Radio value="project">Project</Radio>
              <Radio value="category">Category</Radio>
            </Radio.Group>
          </div>
          {formData.budgetType === "project" && (
            <div className="mb-3">
              <label className="form-label">Select Project</label>
              <Select
                showSearch
                placeholder="Select project"
                className="form-control p-0"
                value={formData.project_id}
                onChange={(value) => handleChange("project_id", value)}
                options={activeProjectsData.map((project: any) => ({
                  label: project.project_name,
                  value: project.project_id,
                }))}
                optionFilterProp="label"
              />
            </div>
          )}

          {formData.budgetType === "category" && (
            <div className="mb-3">
              <label className="form-label">Select Category</label>
              <Select
                showSearch
                placeholder="Select category"
                className="form-control p-0"
                value={formData.category_id}
                onChange={(value) => handleChange("category_id", value)}
                options={activecategoriesData.map((cat: any) => ({
                  label: cat.category,
                  value: cat.expenses_categories_id,
                }))}
                optionFilterProp="label"
              />
            </div>
          )}

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Start Date</label>
              <div className="input-icon-end position-relative">
                <DatePicker
                  className={`form-control datetimepicker ${errors.startDate ? "is-invalid" : ""}`}
                  value={formData.startDate}
                  onChange={(date) => handleChange("startDate", date)}
                  getPopupContainer={getModalContainer}
                  placeholder="DD-MM-YYYY"
                  format="DD-MM-YYYY"
                />
                <span className="input-icon-addon-second">
                  <i className="ti ti-calendar text-gray-7" />
                </span>
              </div>
              {errors.startDate && <div className="text-danger mt-1">{errors.startDate}</div>}
              
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">End Date</label>
              <div className="input-icon-end position-relative">
                <DatePicker
                  className={`form-control datetimepicker ${errors.endDate ? "is-invalid" : ""}`}
                  value={formData.endDate}
                  onChange={(date) => handleChange("endDate", date)}
                  getPopupContainer={getModalContainer}
                  placeholder="DD-MM-YYYY"
                  format="DD-MM-YYYY"
                />
                <span className="input-icon-addon-second">
                  <i className="ti ti-calendar text-gray-7" />
                </span>
              </div>
              {errors.endDate && <div className="text-danger mt-1">{errors.endDate}</div>}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Overall Revenue</label>
            <input
              type="number"
              className={`form-control ${errors.overallRevenue ? "is-invalid" : ""}`}
              value={formData.overallRevenue}
              onChange={(e) => handleChange("overallRevenue", e.target.value)}
            />
            {errors.overallRevenue && <div className="invalid-feedback">{errors.overallRevenue}</div>}
          </div>

          <div className="mb-3">
            <label className="form-label">Overall Expense</label>
            <input
              type="number"
              className={`form-control ${errors.overallExpense ? "is-invalid" : ""}`}
              value={formData.overallExpense}
              onChange={(e) => handleChange("overallExpense", e.target.value)}
            />
            {errors.overallExpense && <div className="invalid-feedback">{errors.overallExpense}</div>}
          </div>

          <div className="mb-3">
            <label className="form-label">Tax Amount</label>
            <input
              type="number"
              className={`form-control ${errors.tax ? "is-invalid" : ""}`}
              value={formData.tax}
              onChange={(e) => handleChange("tax", e.target.value)}
            />
            {errors.tax && <div className="invalid-feedback">{errors.tax}</div>}
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Status</label>
          <Select
            className={`form-control p-0 ${errors.status ? "is-invalid-select" : ""}`}
            value={formData.status}
            onChange={(value) => handleChange("status", value)}
            options={statusOptions}
            placeholder="Select status"
          />
          {errors.status && <div className="text-danger mt-1">{errors.status}</div>}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-light me-2" onClick=
            {() => {
              handleCancel();
              resetForm();
            }}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
            {modalType === "edit" ? "Update Budget" : "Add Budget"}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default BudgetsModal;
